import { BreadCrumb } from "@components/breadCrumb";
import { BreadCrumbWrapper } from "@components/breadCrumb/BreadCrumb.styles";
import { Button, ButtonGroup } from "@components/button";
import FastEntryList from "@components/fastEntry";
import FormGroup from "@components/formGroup";
import Header from "@components/header";
import { LOADING_ERROR_DELAY, LoadingError } from "@components/smart/loadingError/LoadingError";
import SmartFormGroup, { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import Table from "@components/table/Table";
import Tabs from "@components/tabs";
import BindingContext from "@odata/BindingContext";
import { WithOData, withOData } from "@odata/withOData";
import { logger } from "@utils/log";
import memoizeOneWithParamsCheck from "@utils/memoizeOne";
import React from "react";

import { FormStorage } from "../../../views/formView/FormStorage";
import { FormContent, FormFooter } from "../../../views/formView/FormView.styles";
import View from "../../../views/View";

interface IProps extends WithOData {
    storage: FormStorage;
    hideBreadcrumbs?: boolean;
}

interface IState {
    loadingError: boolean;
}

class SmartSkeletonForm extends React.Component<IProps, IState> {
    hasDefinition = false;
    interval: NodeJS.Timeout = null;
    timeout: NodeJS.Timeout = null;

    state: IState = {
        loadingError: false
    };

    componentDidMount() {
        this.timeout = setTimeout(() => {
            this.somethingMightHaveHappened();
        }, LOADING_ERROR_DELAY);
    }

    componentWillUnmount() {
        if (this.interval) {
            clearInterval(this.interval);
        }

        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    shouldComponentUpdate(nextProps: Readonly<IProps>): boolean {
        if (!this.hasDefinition && nextProps.storage.data.definition) {
            this.hasDefinition = true;

            return true;
        }

        // It shouldn't happen, but in case some field info is loaded after the form is rendered,
        // and it would cause some of the fields to re-render,
        // this would cause the re-rendered fields animation to be out of sync with the rest of the form.
        // Try to prevent it by not re-rendering the form at all once it was rendered.
        return false;
    }

    somethingMightHaveHappened = (): void => {
        this.interval = setInterval(() => {
            //if (the is no BE loading anymore) {
            this.setState({ loadingError: true });
            logger.error(`Infinite busy indicator detected on url ${window.location.href}`);
            clearInterval(this.interval);
            //}
        }, 5000);
    };

    getBindingContext = memoizeOneWithParamsCheck(() => {
        return this.props.storage.data.bindingContext;
    });

    renderStandardGroup = (group: IFormGroupDef, bc: BindingContext): React.ReactElement => {
        return (
            <SmartFormGroup key={group.id}
                            storage={this.props.storage}
                            bindingContext={bc}
                            def={group}
                            isSkeleton
                            onExpand={null}
                            onLineItemsChange={null}
                            onLineItemsAction={null}
                            onChange={null}
            />
        );
    };

    renderGroup = (group: IFormGroupDef): React.ReactElement => {
        if (group.tabs) {
            return (
                <div>
                    <Tabs isSkeleton/>
                    <Table isSkeleton rows={[]} columns={[]}/>
                </div>
            );
        }

        if (group.table) {
            // probably inside tab, so don't render..
            // change if needed
            return null;
        }

        if (group.lineItems) {
            return (
                <FormGroup isSkeleton>
                    <FastEntryList isSkeleton/>
                </FormGroup>
            );
        }

        // render collection as one row, edit if more rows needed
        if (group.collection) {
            return this.renderStandardGroup(group, this.getBindingContext().navigate(group.collection));
        }

        return this.renderStandardGroup(group, this.getBindingContext());
    };

    renderForm = (): React.ReactElement => {
        // render based on the default variant
        const groups = this.props.storage.data.definition.groups;

        return (
            <FormContent>
                {groups.map((group, index) => {
                    return (
                        <React.Fragment key={index}>
                            {this.renderGroup(group)}
                        </React.Fragment>
                    );
                })}
            </FormContent>
        );
    };

    renderHeader = (): React.ReactElement => {
        if (!this.props.storage.data?.definition?.summary) {
            return null;
        }

        // granularity of the header skeleton could be improved, like number of buttons and summary items
        return (
            <Header isSkeleton/>
        );
    };

    renderFooter = (): React.ReactElement => {
        return (
            <FormFooter>
                <ButtonGroup>
                    <Button isTransparent isSkeleton/>
                    <Button isTransparent isSkeleton/>
                    <Button isSkeleton/>
                </ButtonGroup>
            </FormFooter>
        );
    };

    render() {
        if (!this.props.storage.data?.definition?.groups) {
            return null;
        }

        if (this.state.loadingError) {
            return <LoadingError/>;
        }

        return (
            <>
                {!this.props.hideBreadcrumbs && <BreadCrumbWrapper>
                    <BreadCrumb isSkeleton/>
                </BreadCrumbWrapper>}
                <View hotspotContextId={`smartSkeletonForm-${this.props.storage.data?.definition?.id}`}>
                    {this.renderHeader()}
                    {this.renderForm()}
                    {this.renderFooter()}
                </View>
            </>
        );
    }
}

export default withOData(SmartSkeletonForm);